// components/MobileFaxTable.tsx
"use client"

import React from "react"
import { Link } from "@inertiajs/react"
import { Download, Archive, Trash2, Redo, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import type { FaxTableRow } from "@/types"


export interface MobileFaxTableProps {
  faxes: FaxTableRow[]
  isMobile: boolean
  showFromColumn: boolean
  showActions?: boolean
  onArchive?: (id: number) => void
  onDelete?: (id: number) => void
  onOpenPDF?: (id: number) => void
  onReprocess?: (id: number) => void
}

/**
 * MobileFaxTable - renders a stacked list of Gmail-style cards
 */
export function MobileFaxTable({
  faxes,
  showFromColumn: isInbound,
  showActions = true,
  onArchive,
  onDelete,
  onOpenPDF,
  onReprocess,
}: MobileFaxTableProps) {
  if (!faxes || faxes.length === 0) {
    return (
      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
        No faxes found
      </div>
    )
  }

  return (
    <div className=" space-y-2">
      {faxes.map((fax) => (
        <MobileFaxCard
          key={fax.id}
          fax={fax}
          isInbound={isInbound}
          showActions={showActions}
          onArchive={onArchive}
          onDelete={onDelete}
          onOpenPDF={onOpenPDF}
          onReprocess={onReprocess}
        />
      ))}
    </div>
  )
}

/* ---------------------------- MobileFaxCard ---------------------------- */

function MobileFaxCard({
  fax,
  isInbound,
  showActions,
  onArchive,
  onDelete,
  onOpenPDF,
  onReprocess,
}: {
  fax: FaxTableRow
  isInbound: boolean
  showActions: boolean
  onArchive?: (id: number) => void
  onDelete?: (id: number) => void
  onOpenPDF?: (id: number) => void
  onReprocess?: (id: number) => void
}) {
  const rowHref = fax.id? `/faxes/${fax.id}` : "#"

  const formatTimestamp = (iso?: string | null) => {
    if (!iso) return ""
    const d = new Date(iso)
    const now = new Date()
    const sameDay = d.toDateString() === now.toDateString()
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" })
  }

  const handlePreventNav = (e: React.MouseEvent, cb?: (id: number) => void) => {
    e.preventDefault()
    e.stopPropagation()
    if (cb) cb(fax.id)
  }

  return (
    <Link
      href={rowHref}
      preserveScroll
      className="group block  border border-border bg-card shadow-sm overflow-hidden active:scale-[0.995] transition focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary"
    >
      <div className="flex items-start gap-3 p-3">

        <div className="flex-1 min-w-0">
          {/* header: sender + time */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <div className={cn("truncate font-semibold text-sm", fax.read === false && "text-foreground")}>
                {isInbound ? fax.from : fax.to}
              </div>
              {/* optional small classification/status badge */}
              <div className="hidden sm:inline-block">
                {isInbound ? (
                  <span className="text-xs text-muted-foreground truncate">{fax.classification}</span>
                ) : (
                  <StatusBadge status={fax.status} />
                )}
              </div>
            </div>

            <div className="ml-2 text-xs text-muted-foreground">
              {formatTimestamp(fax.created_at)}
            </div>
          </div>

          {/* summary */}
          <div className="mt-1 text-sm text-muted-foreground truncate">
            {fax.summary || "No summary available"}
          </div>

          {/* footer: pages + actions */}
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-muted-foreground">{fax.page_count} pages</div>

            {/* actions — render only if showActions true */}
            {showActions ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => handlePreventNav(e, onOpenPDF)}
                  title="Open PDF"
                >
                  <Download className="h-4 w-4" />
                </Button>

                {(isInbound || !["sent", "sending"].includes(fax.status)) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => handlePreventNav(e, onReprocess)}
                    title="Reprocess"
                  >
                    <Redo className="h-4 w-4" />
                  </Button>
                )}

                {onArchive && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => handlePreventNav(e, onArchive)}
                    title={fax.archived ? "Unarchive" : "Archive"}
                  >
                    <Archive className={cn("h-4 w-4", fax.archived && "fill-current")} />
                  </Button>
                )}

                {onDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => handlePreventNav(e, onDelete)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}

/* ---------------------------- Helper: StatusBadge ---------------------------- */

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null
  let style = "bg-yellow-100 text-yellow-800"
  if (status === "delivered" || status === "sent") style = "bg-green-100 text-green-800"
  if (status === "failed" || status === "busy") style = "bg-red-100 text-red-800"

  return <Badge className={cn("capitalize text-xs", style)}>{status}</Badge>
}

export default MobileFaxTable

